import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { ChevronDown, Phone, User, CreditCard, Check, ChevronRight, ChevronLeft, Star, Zap, Crown } from 'lucide-react';

// Lista de países con código y bandera
const COUNTRIES = [
  { code: '+54', country: 'AR', flag: '🇦🇷', name: 'Argentina' },
  { code: '+55', country: 'BR', flag: '🇧🇷', name: 'Brasil' },
  { code: '+56', country: 'CL', flag: '🇨🇱', name: 'Chile' },
  { code: '+57', country: 'CO', flag: '🇨🇴', name: 'Colombia' },
  { code: '+52', country: 'MX', flag: '🇲🇽', name: 'México' },
  { code: '+51', country: 'PE', flag: '🇵🇪', name: 'Perú' },
  { code: '+598', country: 'UY', flag: '🇺🇾', name: 'Uruguay' },
  { code: '+595', country: 'PY', flag: '🇵🇾', name: 'Paraguay' },
  { code: '+591', country: 'BO', flag: '🇧🇴', name: 'Bolivia' },
  { code: '+593', country: 'EC', flag: '🇪🇨', name: 'Ecuador' },
  { code: '+58', country: 'VE', flag: '🇻🇪', name: 'Venezuela' },
  { code: '+1', country: 'US', flag: '🇺🇸', name: 'Estados Unidos' },
  { code: '+34', country: 'ES', flag: '🇪🇸', name: 'España' },
];

// Planes disponibles
const PLANS = [
  {
    id: 'inicial',
    name: 'Plan Inicial',
    subtitle: 'Para principiantes',
    price: 100000,
    priceType: 'mes',
    credits: 4,
    icon: Star,
    color: 'blue',
    features: [
      '4 clases mensuales (1 por semana)',
      '1 crédito por clase',
      'Acceso limitado al sistema de reservas',
      'Ideal para quienes comienzan'
    ],
    recommended: false
  },
  {
    id: 'basic',
    name: 'Plan Basic',
    subtitle: 'Jugadores ocasionales',
    price: 35000,
    priceType: 'hora',
    credits: 0,
    icon: Zap,
    color: 'green',
    features: [
      'Sin compromiso mensual',
      '1 crédito = 1 hora de práctica',
      'Para jugadores con handicap activo',
      'Ideal para jugadores eventuales',
      'Comprá créditos según necesites'
    ],
    recommended: false
  },
  {
    id: 'socio',
    name: 'Plan Socio',
    subtitle: 'Acceso completo',
    price: null,
    priceType: 'mes',
    credits: 5,
    icon: Crown,
    color: 'purple',
    features: [
      '5 horas mensuales incluidas',
      'Seguimiento de juego con IA',
      'Acceso exclusivo a análisis de swing',
      'Informes de entrenamiento personalizado',
      'Seguimiento de tu progreso'
    ],
    recommended: true
  }
];

const PhoneInput = ({ value, onChange, error }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(COUNTRIES[0]);
  const [phoneNumber, setPhoneNumber] = useState('');

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setIsOpen(false);
    if (phoneNumber) {
      onChange(country.code + phoneNumber);
    }
  };

  const handlePhoneChange = (e) => {
    const cleaned = e.target.value.replace(/\D/g, '');
    setPhoneNumber(cleaned);
    onChange(selectedCountry.code + cleaned);
  };

  return (
    <div className="space-y-2">
      <Label htmlFor="whatsapp" className="flex items-center gap-2">
        <Phone className="h-4 w-4" />
        WhatsApp <span className="text-red-500">*</span>
      </Label>
      <div className="flex gap-2">
        <div className="relative">
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 rounded-md h-10 min-w-[120px] hover:bg-white/10 transition-colors"
          >
            <span className="text-xl">{selectedCountry.flag}</span>
            <span className="text-sm">{selectedCountry.code}</span>
            <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>
          
          {isOpen && (
            <div className="absolute top-full left-0 mt-1 w-64 bg-background border border-white/10 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
              {COUNTRIES.map((country) => (
                <button
                  key={country.code}
                  type="button"
                  onClick={() => handleCountrySelect(country)}
                  className={`w-full flex items-center gap-3 px-3 py-2 hover:bg-white/10 transition-colors ${
                    selectedCountry.code === country.code ? 'bg-primary/20' : ''
                  }`}
                >
                  <span className="text-xl">{country.flag}</span>
                  <span className="flex-1 text-left text-sm">{country.name}</span>
                  <span className="text-sm text-muted-foreground">{country.code}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        
        <Input
          id="whatsapp"
          type="tel"
          value={phoneNumber}
          onChange={handlePhoneChange}
          placeholder="11 2345 6789"
          required
          className="flex-1 bg-white/5 border-white/10"
          data-testid="register-whatsapp-input"
        />
      </div>
      
      <p className="text-xs text-muted-foreground">
        Ingresá tu número sin el 0 inicial ni el 15. Ej: 11 2345 6789
      </p>
      
      {phoneNumber && (
        <p className="text-xs text-primary">
          Número completo: {selectedCountry.code} {phoneNumber}
        </p>
      )}
      
      {error && (
        <p className="text-xs text-red-500">{error}</p>
      )}
    </div>
  );
};

// Componente de barra de progreso
const ProgressBar = ({ currentStep, steps }) => {
  return (
    <div className="w-full mb-8">
      {/* Barra de progreso visual */}
      <div className="flex items-center justify-between relative">
        {/* Línea de fondo */}
        <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-full h-1 bg-white/10 rounded-full" />
        
        {/* Línea de progreso */}
        <div 
          className="absolute left-0 top-1/2 transform -translate-y-1/2 h-1 bg-primary rounded-full transition-all duration-500"
          style={{ width: `${((currentStep - 1) / (steps.length - 1)) * 100}%` }}
        />
        
        {/* Pasos */}
        {steps.map((step, index) => {
          const stepNumber = index + 1;
          const isActive = stepNumber === currentStep;
          const isCompleted = stepNumber < currentStep;
          const Icon = step.icon;
          
          return (
            <div key={step.id} className="flex flex-col items-center relative z-10">
              <div 
                className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
                  isCompleted 
                    ? 'bg-primary border-primary text-primary-foreground' 
                    : isActive 
                      ? 'bg-primary/20 border-primary text-primary' 
                      : 'bg-background border-white/20 text-muted-foreground'
                }`}
              >
                {isCompleted ? (
                  <Check className="h-6 w-6" />
                ) : (
                  <Icon className="h-5 w-5" />
                )}
              </div>
              <span className={`mt-2 text-xs font-medium ${
                isActive ? 'text-primary' : isCompleted ? 'text-foreground' : 'text-muted-foreground'
              }`}>
                {step.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Paso 1: Datos personales
const Step1Data = ({ formData, setFormData, errors }) => {
  return (
    <div className="space-y-5">
      <div>
        <Label htmlFor="name">Nombre Completo <span className="text-red-500">*</span></Label>
        <Input
          id="name"
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
          className="mt-1 bg-white/5 border-white/10"
          data-testid="register-name-input"
          placeholder="Juan Pérez"
        />
      </div>

      <div>
        <Label htmlFor="email">Email <span className="text-red-500">*</span></Label>
        <Input
          id="email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          required
          className="mt-1 bg-white/5 border-white/10"
          data-testid="register-email-input"
          placeholder="juan@ejemplo.com"
        />
      </div>

      <PhoneInput 
        value={formData.whatsappNumber}
        onChange={(value) => setFormData({ ...formData, whatsappNumber: value })}
        error={errors.phone}
      />

      <div>
        <Label htmlFor="password">Contraseña <span className="text-red-500">*</span></Label>
        <Input
          id="password"
          type="password"
          value={formData.password}
          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
          required
          minLength={6}
          className="mt-1 bg-white/5 border-white/10"
          data-testid="register-password-input"
          placeholder="••••••••"
        />
        <p className="text-xs text-muted-foreground mt-1">Mínimo 6 caracteres</p>
      </div>
    </div>
  );
};

// Paso 2: Selección de plan
const Step2Plan = ({ formData, setFormData }) => {
  return (
    <div className="space-y-4">
      <p className="text-center text-muted-foreground mb-6">
        Elegí el plan que mejor se adapte a tu nivel de juego
      </p>
      
      <div className="grid gap-4">
        {PLANS.map((plan) => {
          const Icon = plan.icon;
          const isSelected = formData.plan === plan.id;
          
          return (
            <div
              key={plan.id}
              onClick={() => setFormData({ ...formData, plan: plan.id })}
              className={`relative p-5 rounded-xl border-2 cursor-pointer transition-all ${
                isSelected 
                  ? plan.color === 'blue' ? 'border-blue-500 bg-blue-500/10' 
                  : plan.color === 'green' ? 'border-green-500 bg-green-500/10'
                  : 'border-purple-500 bg-purple-500/10'
                  : 'border-white/10 hover:border-white/30 bg-white/5'
              } ${plan.recommended ? 'ring-2 ring-purple-500 ring-offset-2 ring-offset-background' : ''}`}
              data-testid={`plan-${plan.id}`}
            >
              {plan.recommended && (
                <span className="absolute -top-3 left-1/2 transform -translate-x-1/2 px-3 py-1 bg-purple-500 text-white text-xs font-bold rounded-full">
                  RECOMENDADO
                </span>
              )}
              
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-full ${
                  plan.color === 'blue' ? 'bg-blue-500/20' :
                  plan.color === 'green' ? 'bg-green-500/20' :
                  'bg-purple-500/20'
                }`}>
                  <Icon className={`h-6 w-6 ${
                    plan.color === 'blue' ? 'text-blue-400' :
                    plan.color === 'green' ? 'text-green-400' :
                    'text-purple-400'
                  }`} />
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-bold text-lg">{plan.name}</h3>
                      <p className="text-sm text-muted-foreground">{plan.subtitle}</p>
                    </div>
                    <div className="text-right">
                      {plan.price ? (
                        <>
                          <p className="text-xl font-bold">${plan.price.toLocaleString('es-AR')}</p>
                          <p className="text-xs text-muted-foreground">/{plan.priceType}</p>
                        </>
                      ) : (
                        <p className="text-lg font-medium text-purple-400">Consultar</p>
                      )}
                    </div>
                  </div>
                  
                  {plan.credits > 0 && (
                    <p className={`text-sm mt-2 font-medium ${
                      plan.color === 'blue' ? 'text-blue-400' :
                      plan.color === 'green' ? 'text-green-400' :
                      'text-purple-400'
                    }`}>
                      {plan.credits} {plan.id === 'socio' ? 'horas mensuales incluidas' : 'clases mensuales'}
                    </p>
                  )}
                  
                  <ul className="mt-3 space-y-1">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="text-sm text-muted-foreground flex items-center gap-2">
                        <Check className="h-3 w-3 text-primary flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Radio visual */}
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  isSelected 
                    ? plan.color === 'blue' ? 'border-blue-500 bg-blue-500' 
                    : plan.color === 'green' ? 'border-green-500 bg-green-500'
                    : 'border-purple-500 bg-purple-500'
                    : 'border-white/30'
                }`}>
                  {isSelected && <Check className="h-4 w-4 text-white" />}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Paso 3: Confirmación
const Step3Confirm = ({ formData }) => {
  const selectedPlan = PLANS.find(p => p.id === formData.plan);
  const Icon = selectedPlan?.icon || User;
  
  return (
    <div className="space-y-6">
      <p className="text-center text-muted-foreground mb-4">
        Revisá tus datos antes de crear tu cuenta
      </p>
      
      {/* Resumen de datos */}
      <Card className="bg-white/5 border-white/10">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <User className="h-4 w-4" /> Datos Personales
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Nombre:</span>
            <span className="font-medium">{formData.name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Email:</span>
            <span className="font-medium">{formData.email}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">WhatsApp:</span>
            <span className="font-medium">{formData.whatsappNumber}</span>
          </div>
        </CardContent>
      </Card>
      
      {/* Resumen del plan */}
      <Card className="bg-white/5 border-white/10">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Icon className="h-4 w-4" /> Plan Seleccionado
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-bold text-lg">{selectedPlan?.name}</p>
              <p className="text-sm text-muted-foreground">{selectedPlan?.subtitle}</p>
              {selectedPlan?.credits > 0 && (
                <p className="text-sm text-primary mt-1">
                  {selectedPlan.credits} {selectedPlan.id === 'socio' ? 'horas mensuales' : 'clases mensuales'}
                </p>
              )}
            </div>
            <div className="text-right">
              {selectedPlan?.price ? (
                <>
                  <p className="text-2xl font-bold">${selectedPlan.price.toLocaleString('es-AR')}</p>
                  <p className="text-xs text-muted-foreground">/{selectedPlan.priceType}</p>
                </>
              ) : (
                <p className="text-lg text-purple-400 font-medium">Consultar</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Nota sobre pago - IMPORTANTE */}
      <div className="p-4 bg-primary/10 border border-primary/30 rounded-lg">
        <p className="text-sm text-primary font-medium mb-2">
          📋 Importante sobre tu membresía:
        </p>
        <p className="text-sm text-muted-foreground">
          El administrador te cargará tus créditos una vez que reciba tu pago. 
          <strong className="text-foreground"> Comunicate para realizar tu pago.</strong>
        </p>
        <a 
          href="https://wa.me/5493814000000?text=Hola,%20acabo%20de%20registrarme%20y%20quiero%20realizar%20el%20pago%20de%20mi%20membresía"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg text-sm transition-colors"
        >
          <Phone className="h-4 w-4" />
          Contactar por WhatsApp
        </a>
      </div>
      
      {/* Términos */}
      <p className="text-xs text-muted-foreground text-center">
        Al crear tu cuenta, aceptás nuestros términos y condiciones y política de privacidad.
      </p>
    </div>
  );
};

export const Register = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    whatsappNumber: '',
    plan: 'inicial'  // Plan inicial por defecto
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const steps = [
    { id: 'data', label: 'Datos', icon: User },
    { id: 'plan', label: 'Plan', icon: CreditCard },
    { id: 'confirm', label: 'Confirmar', icon: Check }
  ];

  const validateStep1 = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }
    
    if (!formData.password || formData.password.length < 6) {
      newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
    }
    
    const cleaned = formData.whatsappNumber.replace(/\D/g, '');
    if (cleaned.length < 10) {
      newErrors.phone = 'El número debe tener al menos 8 dígitos';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (currentStep === 1) {
      if (!validateStep1()) {
        toast.error('Por favor completá todos los campos correctamente');
        return;
      }
    }
    setCurrentStep(prev => Math.min(prev + 1, 3));
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    setLoading(true);
    
    try {
      await register(formData.email, formData.password, formData.name, formData.whatsappNumber, formData.plan);
      toast.success('¡Cuenta creada exitosamente!');
      
      // Si eligió un plan con costo, mostrar mensaje
      const selectedPlan = PLANS.find(p => p.id === formData.plan);
      if (selectedPlan?.price > 0) {
        toast.info(`Para activar tu ${selectedPlan.name}, realizá el pago desde tu dashboard`, { duration: 6000 });
      }
      
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrarse');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 py-20" data-testid="register-page">
      <div className="max-w-lg w-full">
        {/* Logo y título */}
        <div className="mb-6 text-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_virtualgolf-2/artifacts/si8l522g_IMG_1926.png" 
            alt="City Virtual Golf" 
            className="h-14 w-auto mx-auto mb-3"
          />
          <h1 className="text-2xl sm:text-3xl font-heading font-bold uppercase tracking-tight" data-testid="register-title">
            Crear Cuenta
          </h1>
          <p className="text-muted-foreground mt-1 text-sm" data-testid="register-subtitle">
            {currentStep === 1 && 'Ingresá tus datos personales'}
            {currentStep === 2 && 'Elegí tu plan de entrenamiento'}
            {currentStep === 3 && 'Confirmá y creá tu cuenta'}
          </p>
        </div>

        {/* Barra de progreso */}
        <ProgressBar currentStep={currentStep} steps={steps} />

        {/* Contenido del paso actual */}
        <div className="glass-effect p-6 sm:p-8 rounded-xl">
          {currentStep === 1 && (
            <Step1Data formData={formData} setFormData={setFormData} errors={errors} />
          )}
          
          {currentStep === 2 && (
            <Step2Plan formData={formData} setFormData={setFormData} />
          )}
          
          {currentStep === 3 && (
            <Step3Confirm formData={formData} />
          )}

          {/* Botones de navegación */}
          <div className="flex gap-3 mt-8">
            {currentStep > 1 && (
              <Button 
                type="button"
                variant="outline"
                onClick={handleBack}
                className="flex-1 border-white/20"
                data-testid="register-back-button"
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Atrás
              </Button>
            )}
            
            {currentStep < 3 ? (
              <Button 
                type="button"
                onClick={handleNext}
                className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 font-bold"
                data-testid="register-next-button"
              >
                Siguiente
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button 
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 font-bold py-6"
                data-testid="register-submit-button"
              >
                {loading ? 'Creando cuenta...' : 'Crear Cuenta'}
              </Button>
            )}
          </div>
        </div>

        {/* Link a login */}
        <p className="mt-6 text-center text-muted-foreground text-sm" data-testid="register-login-link">
          ¿Ya tenés cuenta?{' '}
          <Link to="/login" className="text-accent hover:underline font-medium">
            Iniciar sesión
          </Link>
        </p>
      </div>
    </div>
  );
};
